import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { ArrowRight, ArrowLeft, Layers } from "lucide-react";

const SECTION_DESC = {
  foundation: "Your producer business fundamentals — registrations, rights, releases.",
  metadata_authority: "How well your name lives across the metadata authorities that index music credits.",
};

export default function Assessment() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [sections, setSections] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assessment/questions");
        setQuestions(data.questions);
        setSections(data.sections || []);
      } catch (err) { setError(formatApiError(err)); }
    })();
  }, []);

  // Filter out conditional questions whose gate hasn't been answered with the matching value.
  const visibleQuestions = useMemo(() => questions.filter((q) => {
    if (!q.depends_on) return true;
    return answers[q.depends_on.id] === q.depends_on.value;
  }), [questions, answers]);

  if (!questions.length) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-overline">Loading assessment…</div>
        </div>
      </div>
    );
  }

  const total = visibleQuestions.length;
  const safeStep = Math.min(step, Math.max(0, total - 1));
  const current = visibleQuestions[safeStep];
  const progress = ((safeStep + 1) / total) * 100;
  const selected = answers[current.id];
  const prev = safeStep > 0 ? visibleQuestions[safeStep - 1] : null;
  const sectionChanged = !prev || prev.section !== current.section;
  const sectionTitle = sections.find((s) => s.id === current.section)?.title || "";
  const sectionIndex = sections.findIndex((s) => s.id === current.section) + 1;

  const setAnswer = (opt) => setAnswers((a) => ({ ...a, [current.id]: opt }));

  const next = async () => {
    if (!selected) return;
    if (safeStep < total - 1) { setStep(safeStep + 1); return; }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/assessment/submit", { answers });
      navigate("/dashboard", { state: { freshResult: data } });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-24 py-12 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="text-overline">Producer Health Assessment</div>
          <div className="font-mono-tech text-xs text-neutral-500">{String(safeStep + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</div>
        </div>
        <div className="w-full h-px bg-white/5 relative mb-12">
          <div className="absolute left-0 top-0 h-px bg-amber-500 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        {sectionChanged && sectionTitle && (
          <div className="mb-8 p-5 border-l-2 border-amber-500 bg-amber-500/5" data-testid={`section-header-${current.section}`}>
            <div className="flex items-center gap-2 text-overline mb-1">
              <Layers size={14} className="text-amber-500" />
              Section {String(sectionIndex).padStart(2, "0")} · {sectionTitle}
            </div>
            <p className="text-sm text-neutral-300">{SECTION_DESC[current.section] || ""}</p>
          </div>
        )}

        <div className="mb-12" data-testid={`question-${current.id}`}>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight max-w-3xl mb-10">
            {current.question}
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
            {current.options.map((opt) => {
              const active = selected === opt;
              return (
                <button key={opt} data-testid={`option-${current.id}-${opt}`}
                  onClick={() => setAnswer(opt)}
                  className={`text-left px-5 py-5 border transition-all duration-200
                    ${active ? "border-amber-500 bg-amber-500/5 text-white"
                             : "border-white/10 bg-[#121212] text-neutral-200 hover:border-white/30"}`}>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-base">{opt}</span>
                    <span className={`w-3 h-3 border ${active ? "bg-amber-500 border-amber-500" : "border-white/30"}`} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {error && <div data-testid="assessment-error" className="mb-4 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 px-3 py-2">{error}</div>}

        <div className="flex items-center justify-between pt-8 border-t border-white/5">
          <button data-testid="assessment-back-btn" onClick={() => setStep(Math.max(0, safeStep - 1))}
            disabled={safeStep === 0}
            className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
            <ArrowLeft size={16} /> Back
          </button>
          <button data-testid="assessment-next-btn" onClick={next}
            disabled={!selected || busy}
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-black font-semibold hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? "Calculating…" : safeStep === total - 1 ? "See My Score" : "Next"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
